import React, {useEffect, useState} from "react";
import {Image, Platform, Text, TouchableOpacity, View} from "react-native";
import {TextInput} from "react-native-paper";
import {identifierValidator} from "../helpers/identifierValidator";
import {passwordValidator} from "../helpers/passwordValidator";
import {useTheme} from "../helpers/ThemeContext";
import {useThemedStyles} from "../helpers/useThemedStyles";
import {BRAND, withAlpha} from "../helpers/managementTheme";
import axios from "axios";
import * as Device from 'expo-device';
import ToastAlert from "../helpers/ToastAlert";
import {ApiConfig} from "../config/ApiConfig";
import Spinner from "react-native-loading-spinner-overlay";
import {LinearGradient} from "expo-linear-gradient";
import {getAccessToken} from "../helpers/storage";
import {getDeviceTrustToken, getLogoutReason, getOrCreateDeviceTrustId, removeLogoutReason} from "../helpers/storage";
import {useNavigation} from "@react-navigation/native";
import RestaurantPlaceChooser from "../components/RestaurantPlaceChooser";
import {getSetting} from "../helpers/persistentSettings";
import {getRouteForAppMode} from '../config/appVariant';
import { useTranslation } from 'react-i18next';
import twoFactorAuth from '../services/twoFactorAuth';
import TwoFactorChallengeModal from '../components/TwoFactorChallengeModal';
import TwoFactorEnrollmentModal from '../components/TwoFactorEnrollmentModal';
import { isFeatureEnabled } from '../config/featureFlags';
import SocialLoginButtons from '../components/SocialLoginButtons';
import MagicLinkLoginModal from '../components/MagicLinkLoginModal';
import { Ionicons } from '@expo/vector-icons';
import ErrorBoundary from 'react-native-error-boundary';
import ErrorReportingService from '../helpers/ErrorReportingService';
import { enableBiometricLogin, isBiometricTwoFactorEnabled, getBiometricDeviceTrust, authenticateBiometric } from '../helpers/biometricAuth';
import Avatar from '../components/Avatar';
import { setSetting, getSettingJSON } from '../helpers/persistentSettings';

const LogoImage = require("../../assets/logo-small.png");
const Toast_Alert_Position = "top";

const LoginScreen = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const styles = useThemedStyles(buildStyles);
    const [email, setEmail] = useState({ value: __DEV__ ? 'pincer@demoetterem.hu' : '', error: "" });
    const [password, setPassword] = useState({ value: __DEV__ ? 'HappyProba' : '', error: "" });
    const [loading, setLoading] = useState(false);
    const [deviceInfo, setDeviceInfo] = useState({});
    const [savedData, setSavedData] = useState({});
    const [challengePayload, setChallengePayload] = useState(null);
    const [showChallengeModal, setShowChallengeModal] = useState(false);
    const [pendingLoginData, setPendingLoginData] = useState(null);
    const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
    const [enrollmentRequired, setEnrollmentRequired] = useState(false);
    const [showMagicLink, setShowMagicLink] = useState(false);
    // Why the previous session ended (if it was force-ended) — drives a one-time
    // professional banner explaining the sign-out. Cleared as soon as it is read.
    const [sessionNotice, setSessionNotice] = useState(null);

    useEffect(() => {
        let active = true;

        getLogoutReason()
            .then((reason) => {
                if (active && reason) {
                    setSessionNotice(reason);
                    removeLogoutReason();
                }
            })
            .catch(() => { /* no banner on read failure */ });

        return () => { active = false; };
    }, []);
    const [lastUser, setLastUser] = useState(null);

    const navigation = useNavigation();

    React.useEffect(() => {
        const checkLogin = async () => {
            if (await getAccessToken()) {
                const userAppMode = await getSetting('userAppMode');
                navigation.navigate(getRouteForAppMode(userAppMode));
            }
        };
        checkLogin();
    }, [navigation]);

    React.useEffect(() => {
        const getDeviceInfo = async () => {
            const deviceName = Device.deviceName;
            const deviceId = Device.osBuildId;
            const systemVersion = Device.osVersion;
            setDeviceInfo({ deviceName, deviceId, systemVersion });
        };

        getDeviceInfo();
    }, []);

    // Greet whoever last signed in on this device. After a logout / app update
    // the login screen shows their name + avatar and pre-fills the identifier,
    // with a one-tap escape hatch to sign in as somebody else.
    React.useEffect(() => {
        let active = true;

        (async () => {
            const stored = await getSettingJSON('lastLoggedInUser', null);

            if (active && stored && (stored.email || stored.name)) {
                setLastUser(stored);
                if (stored.email) {
                    setEmail({ value: stored.email, error: '' });
                }
            }
        })();

        return () => { active = false; };
    }, []);

    const resolveDisplayName = (u) => {
        if (!u) {
            return '';
        }
        if (u.name && typeof u.name === 'object') {
            return `${u.name.first || ''} ${u.name.last || ''}`.trim();
        }
        if (typeof u.name === 'string' && u.name.trim()) {
            return u.name.trim();
        }
        const composed = `${u.firstName || ''} ${u.lastName || ''}`.trim();

        return composed || u.email || '';
    };

    // Drop the remembered user and reveal the blank identifier field so a
    // different account can sign in on the same device.
    const switchToDifferentUser = () => {
        setLastUser(null);
        setEmail({ value: '', error: '' });
        setPassword({ value: '', error: '' });
    };

    const continueAfterLogin = async (loginData) => {
        const mustEnroll = !!(loginData?.mustEnroll2FA || loginData?.user?.mustEnroll2FA);

        if (!loginData?.token || !loginData?.user?._id) {
            return false;
        }

        // Stash the session (keychain) so future visits can unlock with Face ID / Touch ID.
        enableBiometricLogin(loginData.token, loginData.user).catch(() => {});

        // Remember who signed in so the login screen can greet them by name next
        // time (survives logout / app updates). Only a tiny display subset is kept.
        setSetting('lastLoggedInUser', {
            _id: loginData.user?._id,
            name: loginData.user?.name,
            email: loginData.user?.email,
            photo: loginData.user?.photo
                || loginData.user?.avatarUrl
                || loginData.user?.avatar
                || loginData.user?.image
                || null,
        }).catch(() => {});

        try {
            const status = await twoFactorAuth.getStatus(loginData.token);

            if (!status?.enabled) {
                setPendingLoginData(loginData);
                setEnrollmentRequired(mustEnroll);
                setShowEnrollmentModal(true);

                return true;
            }
        } catch {
            // If status endpoint fails, do not block login.
        }

        setSavedData(loginData);

        return true;
    };

    const _loginAction = async () => {
        setLoading(true);

        if (!email.value) {
            setLoading(false);
            return ToastAlert(
                "error",
                Toast_Alert_Position,
                t('login_screen_title', { defaultValue: 'Login' }),
                t('email_required', { defaultValue: 'Email or phone number is required.' }),
                5000
            );
        }
        if (!password.value) {
            setLoading(false);
            return ToastAlert(
                "error",
                Toast_Alert_Position,
                t('login_screen_title', { defaultValue: 'Login' }),
                t('password_required', { defaultValue: 'Password is required.' }),
                5000
            );
        }

        if (email.error || password.error) {
            setLoading(false);
            return false;
        }

        try {
            const deviceTrustId = await getOrCreateDeviceTrustId();

            // "Remember this device" bypass token. When biometric 2FA is enrolled
            // on this device the token lives in the keychain and is released only
            // after a local Face ID / Touch ID / passcode prompt — so the biometric
            // is the second factor. On cancel/failure we omit it and fall through
            // to the normal 2FA challenge (TOTP / SMS / email). Otherwise fall back
            // to the plain (silent) "remember this device" token.
            let deviceTrustToken;

            if (await isBiometricTwoFactorEnabled()) {
                const ok = await authenticateBiometric(
                    t('two_factor_biometric_prompt', { defaultValue: 'Confirm your identity to sign in' })
                );

                deviceTrustToken = ok ? await getBiometricDeviceTrust() : undefined;
            } else {
                deviceTrustToken = await getDeviceTrustToken();
            }
            const { data } = await axios.post(
                ApiConfig.apiURL + "/auth/local",
                {
                    email: email.value,
                    password: password.value,
                    // "Remember this device" — lets the backend skip 2FA for trusted devices.
                    deviceTrustToken: deviceTrustToken || undefined,
                    deviceId: deviceTrustId || undefined,
                },
                {
                    // Without a timeout an unreachable/stalled backend leaves the
                    // request pending forever, so `loading` is never reset and the
                    // "Signing in…" overlay spins indefinitely. Fail fast instead.
                    timeout: 20000,
                    // Device hints belong in the HTTP headers (3rd arg / config),
                    // not in the POST body — previously they were sent as body
                    // fields and the backend never received them.
                    headers: {
                        "Accept-Language": "hu_HU",
                        "x-device-id": deviceInfo.deviceId,
                        "x-device-name": `${deviceInfo.deviceName} / ${deviceInfo.systemVersion}`,
                    },
                }
            );

            if (data?.requires2FA && data?.preAuthToken) {
                setLoading(false);
                setChallengePayload(data);
                setShowChallengeModal(true);

                return;
            }

            if (data.token && data.user?._id) {
                setLoading(false);
                await continueAfterLogin(data);
            } else {
                setLoading(false);
                return ToastAlert(
                    "error",
                    Toast_Alert_Position,
                        t('login_screen_title', { defaultValue: 'Login' }),
                        t('invalid_login_credentials', { defaultValue: 'Invalid email/phone or password.' }),
                    5000
                );
            }
        } catch (error) {
            setLoading(false);

            // A timeout (ECONNABORTED) or a request that never got a response
            // (no `error.response`) is a connectivity problem, not bad
            // credentials — tell the user so they don't keep retrying a valid
            // password against an unreachable server.
            const isNetworkError =
                error?.code === 'ECONNABORTED' || !error?.response;
            if (isNetworkError) {
                return ToastAlert(
                    "error",
                    Toast_Alert_Position,
                    t('login_screen_title', { defaultValue: 'Login' }),
                    t('login_network_error', { defaultValue: 'Cannot reach the server. Check your internet connection and try again.' }),
                    5000
                );
            }

            const status = error?.response?.status;
            const data = error?.response?.data || {};

            // 423 Locked — the account is temporarily or indefinitely locked after
            // too many failed attempts.
            if (status === 423 || data?.code === 'ACCOUNT_LOCKED') {
                if (data?.indefinite) {
                    return ToastAlert(
                        "error",
                        Toast_Alert_Position,
                        t('login_locked_title', { defaultValue: 'Account locked' }),
                        t('login_locked_indefinite', { defaultValue: 'Your account has been locked for security. Reset your password using "Forgotten password" or contact an administrator to regain access.' }),
                        8000
                    );
                }
                // Prefer an explicit retry window from the server; fall back to the
                // configured lock duration, then to a generic message.
                const minutes = data?.retryAfterSeconds
                    ? Math.max(1, Math.ceil(data.retryAfterSeconds / 60))
                    : (data?.lockDurationMinutes || null);
                return ToastAlert(
                    "error",
                    Toast_Alert_Position,
                    t('login_locked_title', { defaultValue: 'Account locked' }),
                    minutes
                        ? t('login_locked_timed', { minutes, defaultValue: 'Too many failed attempts. Your account is locked. Try again in {{minutes}} minutes.' })
                        : t('login_locked_generic', { defaultValue: 'Too many failed attempts. Your account is temporarily locked. Please try again later.' }),
                    8000
                );
            }

            // 422 Invalid credentials — surface how many attempts remain before
            // the account gets locked.
            if (status === 422 || data?.code === 'INVALID_CREDENTIALS') {
                const remaining = typeof data?.attemptsRemaining === 'number' ? data.attemptsRemaining : null;
                return ToastAlert(
                    "error",
                    Toast_Alert_Position,
                    t('login_screen_title', { defaultValue: 'Login' }),
                    remaining !== null && remaining >= 0
                        ? t('login_attempts_remaining', { count: remaining, defaultValue: '{{count}} attempts remaining before your account is locked.' })
                        : t('invalid_login_credentials', { defaultValue: 'Invalid email/phone or password.' }),
                    5000
                );
            }

            return ToastAlert(
                "error",
                Toast_Alert_Position,
                t('login_screen_title', { defaultValue: 'Login' }),
                data?.message || t('invalid_login_credentials', { defaultValue: 'Invalid email/phone or password.' }),
                5000
            );
        }
    };

    const onLoginPressed = () => {
        const emailError = identifierValidator(email.value);
        const passwordError = passwordValidator(password.value);
        if (emailError || passwordError) {
            setEmail({...email, error: emailError});
            setPassword({...password, error: passwordError});
            return;
        }
        return _loginAction();
    };

    return (
        <LinearGradient
            // Brand orange hero — the white form card floats on top of it.
            colors={BRAND.gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.background}
        >
            <View pointerEvents="none" style={styles.blobA} />
            <View pointerEvents="none" style={styles.blobB} />
            <View style={styles.container}>
                <Spinner
                    visible={loading}
                    textContent={t('login_in_progress', { defaultValue: 'Signing in...' })}
                    textStyle={styles.spinnerTextStyle}
                />
                <View style={styles.logoPlate}>
                    {Platform.OS !== "web" && <Image
                        source={LogoImage}
                        style={styles.logo}
                        resizeMode="contain"
                    />}
                    {Platform.OS === "web" && <img
                        alt={"Happy Solutions Logo"}
                        src={'https://happy-releases.fra1.cdn.digitaloceanspaces.com/Uploads/logo.png'}
                        style={styles.logo}
                    />}
                </View>
                {savedData?.token && savedData?.user?._id && <RestaurantPlaceChooser data={savedData} onDismiss={() => setSavedData({})} />}
                {sessionNotice && (() => {
                    const noticeCopy = {
                        'session-expired': {
                            title: t('session_expired_title', { defaultValue: 'Session expired' }),
                            message: t('session_expired_message', { defaultValue: 'For your security, you were signed out. Please sign in again.' }),
                        },
                        'access-revoked': {
                            title: t('access_revoked_title', { defaultValue: 'Access revoked' }),
                            message: t('access_revoked_message', { defaultValue: 'System usage rights revoked. Please contact your administrator.' }),
                        },
                        'device-logged-out': {
                            title: t('device_logged_out_title', { defaultValue: 'Device signed out' }),
                            message: t('device_logged_out_message', { defaultValue: 'This device was signed out remotely. Please sign in again.' }),
                        },
                        'account-inactive': {
                            title: t('account_inactive_title', { defaultValue: 'Account inactive' }),
                            message: t('account_inactive_message', { defaultValue: 'Your account is no longer active. Please contact your administrator.' }),
                        },
                    }[sessionNotice];

                    if (!noticeCopy) {
                        return null;
                    }

                    const accent = sessionNotice === 'access-revoked' ? '#DC2626' : BRAND.dark;

                    return (
                        <View style={[styles.notice, { borderLeftColor: accent }]}>
                            <Text style={{ fontWeight: '800', color: accent, marginBottom: 3 }}>{noticeCopy.title}</Text>
                            <Text style={{ color: theme.colors.subText, fontSize: 13, lineHeight: 18 }}>{noticeCopy.message}</Text>
                        </View>
                    );
                })()}
                <View style={styles.welcome}>
                    <Text style={styles.welcomeTitle}>{t('login_welcome_title', { defaultValue: 'Welcome back' })}</Text>
                    <Text style={styles.welcomeSubtitle}>{t('login_welcome_subtitle', { defaultValue: 'Sign in to your Happy Solutions workspace.' })}</Text>
                </View>
                <View style={styles.formCard}>
                    {lastUser ? (
                        <View style={styles.returningUser}>
                            <Avatar user={lastUser} size={54} />
                            <View style={styles.returningUserMeta}>
                                <Text style={styles.returningUserName} numberOfLines={1}>{resolveDisplayName(lastUser)}</Text>
                                {!!lastUser.email && (
                                    <Text style={styles.returningUserEmail} numberOfLines={1}>{lastUser.email}</Text>
                                )}
                            </View>
                        </View>
                    ) : (
                        <TextInput
                            name="email"
                            mode="outlined"
                            label={t('email_or_phone', { defaultValue: 'Email or phone number' })}
                            returnKeyType="next"
                            value={email.value}
                            onChangeText={(text) => setEmail({value: text, error: ""})}
                            error={!!email.error}
                            errorText={email.error}
                            autoCapitalize="none"
                            autoCompleteType="email"
                            textContentType="emailAddress"
                            keyboardType="email-address"
                            outlineColor={theme.colors.border}
                            activeOutlineColor={BRAND.base}
                            outlineStyle={styles.inputOutline}
                            style={styles.input}
                        />
                    )}
                    <TextInput
                        name="password"
                        mode="outlined"
                        label={t('password', { defaultValue: 'Password' })}
                        returnKeyType="done"
                        value={password.value}
                        onChangeText={(text) => setPassword({value: text, error: ""})}
                        error={!!password.error}
                        errorText={password.error}
                        secureTextEntry
                        outlineColor={theme.colors.border}
                        activeOutlineColor={BRAND.base}
                        outlineStyle={styles.inputOutline}
                        style={styles.input}
                    />
                    {/* Both links are build-time switchable (config/featureFlags.js)
                        so a trimmed release can ship a login screen with nothing
                        but the e-mail + password form. */}
                    {(isFeatureEnabled('auth.guestChat') || isFeatureEnabled('auth.passwordReset')) && <View style={styles.forgotPassword}>
                        {isFeatureEnabled('auth.guestChat') && (
                            <TouchableOpacity onPress={() => navigation.navigate("GuestSupportChatScreen")}>
                                <Text style={styles.needHelp}>{t('need_help_link', { defaultValue: 'Need help?' })}</Text>
                            </TouchableOpacity>
                        )}
                        {isFeatureEnabled('auth.passwordReset') && (
                            <TouchableOpacity onPress={() => navigation.navigate("ForgotPasswordScreen")}>
                                <Text style={styles.forgot}>{t('forgot_password', { defaultValue: 'Forgot password?' })}</Text>
                            </TouchableOpacity>
                        )}
                    </View>}
                    {lastUser && (
                        <TouchableOpacity onPress={switchToDifferentUser} style={styles.switchUser}>
                            <Text style={styles.switchUserText}>{t('login_sign_in_as_different_user', { defaultValue: 'Sign in as a different user' })}</Text>
                        </TouchableOpacity>
                    )}
                    <TouchableOpacity activeOpacity={0.9} onPress={onLoginPressed}>
                        <LinearGradient
                            colors={BRAND.gradient}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.loginButton}
                        >
                            <Text style={styles.loginButtonText}>{t('login', { defaultValue: 'Login' })}</Text>
                        </LinearGradient>
                    </TouchableOpacity>
                    {isFeatureEnabled('auth.magicLink') && (
                        <TouchableOpacity
                            activeOpacity={0.85}
                            onPress={() => setShowMagicLink(true)}
                            style={styles.magicLinkButton}
                        >
                            <Ionicons name="mail-outline" size={18} color={BRAND.dark} />
                            <Text style={styles.magicLinkButtonText}>
                                {t('magic_link_login_button', { defaultValue: 'Sign in with an email code' })}
                            </Text>
                        </TouchableOpacity>
                    )}
                    <View style={styles.secureRow}>
                        <Text style={styles.secureText}>{t('login_secure_note', { defaultValue: '🔒 Protected by two-factor authentication' })}</Text>
                    </View>
                    {/* The social block reaches native modules (expo-auth-session
                        / expo-web-browser / expo-apple-authentication) whose methods
                        can be undefined in a partially-linked release archive — a
                        render-time "undefined is not a function" that otherwise takes
                        the ENTIRE sign-in screen down. Isolate it: on failure the
                        buttons vanish and email/password sign-in keeps working. */}
                    <ErrorBoundary
                        FallbackComponent={() => null}
                        onError={(error) => {
                            ErrorReportingService.reportError(error, {
                                source: 'SocialLoginButtons',
                                phase: 'render',
                            });
                        }}
                    >
                        <SocialLoginButtons
                            t={t}
                            setLoading={setLoading}
                            onAuthenticated={continueAfterLogin}
                            on2FA={(data) => {
                                setChallengePayload(data);
                                setShowChallengeModal(true);
                            }}
                        />
                    </ErrorBoundary>
                </View>
                {false && <View style={styles.row}>
                    <Text>{t('login_no_account_yet', { defaultValue: 'You do not have an account yet?' })}</Text>
                    <TouchableOpacity onPress={() => navigation.replace("RegisterScreen")}>
                        <Text style={styles.link}>{t('login_create_account', { defaultValue: 'Create!' })}</Text>
                    </TouchableOpacity>
                </View>}
                <MagicLinkLoginModal
                    visible={showMagicLink}
                    initialEmail={lastUser?.email || email.value}
                    t={t}
                    deviceHeaders={{
                        "x-device-id": deviceInfo.deviceId,
                        "x-device-name": `${deviceInfo.deviceName} / ${deviceInfo.systemVersion}`,
                    }}
                    onClose={() => setShowMagicLink(false)}
                    onAuthenticated={continueAfterLogin}
                    on2FA={(data) => {
                        setChallengePayload(data);
                        setShowChallengeModal(true);
                    }}
                />
                <TwoFactorChallengeModal
                    visible={showChallengeModal}
                    payload={challengePayload}
                    t={t}
                    onCancel={() => {
                        setShowChallengeModal(false);
                        setChallengePayload(null);
                    }}
                    onSuccess={async (verified) => {
                        setShowChallengeModal(false);
                        setChallengePayload(null);
                        await continueAfterLogin(verified);
                    }}
                />
                <TwoFactorEnrollmentModal
                    visible={showEnrollmentModal}
                    token={pendingLoginData?.token}
                    t={t}
                    mustEnroll={enrollmentRequired}
                    onSkip={() => {
                        setShowEnrollmentModal(false);
                        if (pendingLoginData) {
                            setSavedData(pendingLoginData);
                        }
                        setPendingLoginData(null);
                    }}
                    onCompleted={() => {
                        setShowEnrollmentModal(false);
                        if (pendingLoginData) {
                            setSavedData(pendingLoginData);
                        }
                        setPendingLoginData(null);
                    }}
                />
            </View>
        </LinearGradient>
    );
};

const buildStyles = (theme, isDark) => ({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
        width: '100%',
    },
    // Soft translucent discs echo the hero card of the management screens.
    blobA: {
        position: 'absolute',
        width: 320,
        height: 320,
        borderRadius: 160,
        backgroundColor: 'rgba(255,255,255,0.12)',
        top: -120,
        right: -90,
    },
    blobB: {
        position: 'absolute',
        width: 240,
        height: 240,
        borderRadius: 120,
        backgroundColor: 'rgba(255,255,255,0.09)',
        bottom: -90,
        left: -70,
    },
    // White plate keeps the logo legible on the orange hero.
    logoPlate: {
        width: 96,
        height: 96,
        borderRadius: 32,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 22,
        shadowColor: BRAND.deep,
        shadowOpacity: 0.28,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 10 },
        elevation: 8,
    },
    logo: {
        width: 68,
        height: 68,
    },
    notice: {
        width: '100%',
        maxWidth: 420,
        marginBottom: 14,
        padding: 14,
        borderRadius: 20,
        borderLeftWidth: 4,
        backgroundColor: isDark ? '#131B2F' : '#FFFFFF',
    },
    welcome: {
        width: '100%',
        maxWidth: 420,
        marginBottom: 18,
    },
    welcomeTitle: {
        fontSize: 30,
        fontWeight: '800',
        letterSpacing: -0.8,
        color: '#FFFFFF',
    },
    welcomeSubtitle: {
        marginTop: 6,
        fontSize: 14.5,
        color: 'rgba(255,255,255,0.9)',
        lineHeight: 20,
    },
    formCard: {
        width: '100%',
        maxWidth: 420,
        backgroundColor: isDark ? '#131B2F' : '#FFFFFF',
        borderRadius: 28,
        borderWidth: 1,
        borderColor: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(255,255,255,0.9)',
        padding: 24,
        shadowColor: '#7C2D12',
        shadowOpacity: 0.22,
        shadowRadius: 28,
        shadowOffset: { width: 0, height: 16 },
        elevation: 10,
    },
    input: {
        width: '100%',
        marginBottom: 12,
        backgroundColor: isDark ? '#131B2F' : '#FFFFFF',
    },
    inputOutline: {
        borderRadius: 16,
        borderWidth: 1.5,
    },
    returningUser: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
        padding: 12,
        borderRadius: 20,
        backgroundColor: withAlpha(BRAND.base, isDark ? 0.14 : 0.08),
        borderWidth: 1,
        borderColor: withAlpha(BRAND.base, isDark ? 0.3 : 0.2),
    },
    returningUserMeta: {
        flex: 1,
        marginLeft: 12,
    },
    returningUserName: {
        fontSize: 16,
        fontWeight: '800',
        color: theme.colors.text,
    },
    returningUserEmail: {
        marginTop: 2,
        fontSize: 13,
        color: theme.colors.subText,
    },
    switchUser: {
        width: '100%',
        alignItems: 'center',
        marginBottom: 8,
    },
    switchUserText: {
        fontSize: 13,
        fontWeight: '800',
        color: BRAND.dark,
    },
    forgotPassword: {
        width: "100%",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 14,
    },
    forgot: {
        fontSize: 13,
        color: BRAND.dark,
        fontWeight: '800',
    },
    needHelp: {
        fontSize: 13,
        color: theme.colors.subText || theme.colors.text,
        fontWeight: '600',
    },
    loginButton: {
        height: 54,
        width: '100%',
        borderRadius: 999,
        marginTop: 4,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: BRAND.dark,
        shadowOpacity: 0.34,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 10 },
        elevation: 8,
    },
    loginButtonText: {
        fontSize: 17,
        fontWeight: '800',
        color: "#FFFFFF",
        textAlign: 'center',
        letterSpacing: 0.2,
    },
    // Secondary (outlined) action under the primary login button.
    magicLinkButton: {
        height: 50,
        width: '100%',
        borderRadius: 999,
        marginTop: 12,
        borderWidth: 1.5,
        borderColor: withAlpha(BRAND.base, 0.45),
        backgroundColor: withAlpha(BRAND.base, isDark ? 0.12 : 0.06),
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    magicLinkButtonText: {
        fontSize: 15,
        fontWeight: '700',
        color: BRAND.dark,
    },
    secureRow: {
        marginTop: 16,
        alignItems: 'center',
    },
    secureText: {
        fontSize: 12,
        color: theme.colors.placeholder,
        fontWeight: '600',
    },
    row: {
        flexDirection: "row",
        marginTop: 4,
    },
    link: {
        fontWeight: "bold",
        color: BRAND.dark,
        marginLeft: 5,
    },
    background: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
    },
    spinnerTextStyle: {
        color: '#FFF'
    },
});

export default LoginScreen;